import type { KnowledgeUnit } from '../types'

// ---------------------------------------------------------------------------
// pages.ts — curated page units (one per analytics page, 6 total)
//
// Draft revision v0 (not yet analyst-greenlit per spec §18).
// Bodies condensed from spec Appendix A. Use-case→page mappings are INFERRED
// where marked; final routing assignments are the agenda for the analyst
// tuning pass post-build.
// ---------------------------------------------------------------------------

export const pages: KnowledgeUnit[] = [
  {
    id: 'page:ReviewKpis',
    type: 'page',
    keys: [
      '/review-status/review-kpis',
      'review kpis',
      'headline',
      'actuals',
      'actual sales',
      'business results',
      'sales performance',
      'overall performance',
    ],
    version: '0.2-draft',
    title: 'Review KPIs',
    summary: 'Headline business results — overall actual sales and key business metrics.',
    body: `Purpose: Review KPIs is the top-level performance health-check page. It answers "how are we doing overall?" using observed (actual) sales and other headline business KPIs. It is sales-led; its charts resolve per-KPI through the data-driven chart registry rather than a single hardcoded fact. The lead actual_sales KPI is served from the kpi_overall fact table.

Lead KPI: actual_sales — the observed total sales figure, not model-attributed decomposition.

Example questions that route HERE:
- "How are sales performing this quarter?"
- "What were our total sales last month vs. the prior month?"
- "Give me a headline summary of business results."
- "Are we hitting our targets?" (INFERRED — confirm with analyst)
- "Show me the overall KPI dashboard."

What does NOT belong here:
- Spend analysis ("how much did we invest?") → Marketing Invests.
- Campaign efficiency / ROI → Campaign Performance.
- Sales decomposition by driver ("what drove sales?") → Contribution.
- Temporal attribution ("why did sales change?") → Dynamics.
- Multi-dimensional drill → Multi Deep Dive.

Boundary note (INFERRED): spend metrics may appear alongside actuals on this page — the analyst will confirm whether spend overlap belongs here or only on Marketing Invests (Appendix A ambiguity #4).`,
    seeAlso: ['kpi:actual_sales'],
  },

  {
    id: 'page:MarketingInvests',
    type: 'page',
    keys: [
      '/review-status/marketing-invests',
      'marketing invests',
      'spend',
      'investment',
      'budget',
      'nsp',
      'net spend',
      'media investment',
      'channel spend',
    ],
    version: '0.1-draft',
    title: 'Marketing Invests',
    summary: 'Spend analysis — how much was invested in media and where.',
    body: `Purpose: Marketing Invests is the spend-ledger page. It answers "how much did we spend, and where?" — pure spend analysis, spend-led, using the media_national fact table.

Lead KPI: nsp (Net Spend) — total net media investment by channel, campaign, or time period.

Example questions that route HERE:
- "How much did we spend on TV last quarter?"
- "What was the media budget breakdown by channel?"
- "Which campaign received the most investment?"
- "Show me spend by region." (INFERRED)
- "How did spend evolve week by week?"

What does NOT belong here:
- Whether that spend delivered returns → Campaign Performance (ROI).
- What share of sales media drove → Contribution (decomposition).
- How efficiency changed over time → Dynamics.
- Actual total sales → Review KPIs.

Boundary note (INFERRED): some spend metrics may also surface on Review KPIs — the analyst will confirm the boundary between the two status pages (Appendix A ambiguity #4).`,
    seeAlso: ['kpi:nsp', 'concept:cpo'],
  },

  {
    id: 'page:CampaignPerformance',
    type: 'page',
    keys: [
      '/review-performance/campaign-performance',
      'campaign performance',
      'roi',
      'efficiency',
      'profitability',
      'sales roi',
      'campaign',
      'model tracking',
      'performance',
      'return',
    ],
    version: '0.1-draft',
    title: 'Campaign Performance',
    summary:
      'Efficiency and profitability — Sales RoI, CpO, and driver attribution per campaign or time period.',
    body: `Purpose: Campaign Performance (CP) is the primary efficiency and profitability page. It answers "how efficiently did campaigns convert spend into sales?" — using ratio KPIs (Sales RoI, CpO, OpC) and driver bars, on the model_tracking fact table. It is the DEEP-SKILL page for the advisor MVP (full read/steer/explain).

Lead KPI: sales_roi__nsp__npv (Sales RoI, NSP driver, NPV value_type).
Fact table: model_tracking. Archetype: performance.
Controls: KPI cascade, dim filters, timeframe, group-by, multi-timeframe (no comparison, no granularity on CP).

Example questions that route HERE:
- "Which campaigns had the best ROI last quarter?"
- "Was Campaign X profitable?"
- "How did Sales RoI change between Q3 and Q4?"
- "What drove the drop in efficiency for brand campaigns?"
- "Compare ROI across regions." (INFERRED)
- "Show me which drivers contributed most to the RoI change."

What does NOT belong here:
- Pure spend totals → Marketing Invests.
- Sales decomposition (baseline vs. media) → Contribution.
- Temporal "what changed and why" narrative → Dynamics (though CP's multi-TF can compare periods).
- Multi-metric / multi-dimension bar drill → Multi Deep Dive.

Boundary ambiguity (CP vs Dynamics — INFERRED): "compare Q3 vs Q4 efficiency" could be answered by CP (multi-TF period bars) OR Dynamics (per-timeframe panels with driver attribution). CP is preferred when the question is campaign-granular; Dynamics when the question is about trend/temporal causation. The analyst will confirm this routing rule.

Boundary ambiguity (CP vs MultiDeepDive — INFERRED): "show RoI across 3 timeframes and 2 regions" = multi-TF bars on CP; "show RoI across campaigns × regions × timeframes simultaneously" = MultiDeepDive.`,
    seeAlso: ['kpi:sales_roi', 'concept:npv', 'concept:cpo'],
  },

  {
    id: 'page:Contribution',
    type: 'page',
    keys: [
      '/review-performance/contribution',
      'contribution',
      'decomposition',
      'what drove sales',
      'sales drivers',
      'channel contribution',
      'media contribution',
      'baseline',
      'attribution',
      'stacked',
    ],
    version: '0.1-draft',
    title: 'Contribution',
    summary:
      'Sales decomposition — what proportion of sales is attributable to media vs. baseline vs. other drivers.',
    body: `Purpose: Contribution answers "what is driving our sales?" — it decomposes total sales into channel-level or driver-group-level contributions (media, baseline, etc.) using the model_success_kpi fact table. Primary visualization: stacked timelines and contribution charts.

Lead KPI: sales__media__units (Sales, media driver, units value_type).
Fact table: model_success_kpi.

Example questions that route HERE:
- "What percentage of sales came from paid media?"
- "How much of our sales is baseline (organic)?"
- "Which channel is contributing most to sales?" (INFERRED — see boundary note)
- "Show me the media vs. baseline split over the last 12 weeks."
- "How has the media contribution share evolved?"

What does NOT belong here:
- ROI / efficiency ("is media cost-efficient?") → Campaign Performance.
- Temporal "what changed and why" → Dynamics.
- Multi-metric drill across dimensions → Multi Deep Dive.
- Headline sales total → Review KPIs.

Boundary ambiguity (Contribution vs MultiDeepDive — INFERRED): "sales broken down by channel AND region" — Contribution for stacked decomposition (one metric, structured by driver); MultiDeepDive for bar comparisons across multiple dimension combinations. Prefer Contribution for "what's driving sales?", MultiDeepDive for "how does X compare across Y and Z?"

Boundary ambiguity ("sales by channel" — INFERRED): routes to Contribution (decomposition/attribution) rather than Dynamics (temporal). Dynamics is preferred when the question is about what CHANGED over time.`,
    seeAlso: ['kpi:sales', 'concept:mediashare'],
  },

  {
    id: 'page:Dynamics',
    type: 'page',
    keys: [
      '/review-performance/dynamics',
      'dynamics',
      'what changed',
      'trend',
      'temporal',
      'driver attribution',
      'why did',
      'change over time',
      'period comparison',
    ],
    version: '0.1-draft',
    title: 'Dynamics',
    summary:
      'Temporal attribution — what changed over time and why, with metric trends and driver panels.',
    body: `Purpose: Dynamics answers "what changed and why?" — it shows how a metric (e.g. Sales RoI) evolved over time and attributes that change to specific drivers (spend levels, channel mix, etc.) using the model_tracking fact table. Primary visualization: trend lines with per-timeframe driver attribution panels.

Lead KPI: sales_roi__nsp__npv (same as Campaign Performance; Dynamics is time-oriented, CP is campaign-granular).
Fact table: model_tracking.

Example questions that route HERE:
- "Why did Sales RoI drop in Q3?"
- "What drove the efficiency improvement last month?"
- "How did the driver mix change over the year?"
- "Which factors caused the metric to shift between periods?"
- "Show the trend in ROI with driver attribution." (INFERRED)

What does NOT belong here:
- Campaign-by-campaign comparison → Campaign Performance.
- Static sales decomposition (overall contribution %) → Contribution.
- Multi-metric / multi-dim bar drill → Multi Deep Dive.

Boundary ambiguity (CP vs Dynamics — INFERRED): "compare Q3 vs Q4 efficiency" → CP for campaign-level period bars; Dynamics for "what caused the change?" temporal narrative. The analyst will finalize this boundary.

Boundary ambiguity ("sales by channel" — INFERRED): Contribution is preferred over Dynamics for pure channel-decomposition questions without a temporal "what changed" framing.`,
    seeAlso: ['kpi:sales_roi'],
  },

  {
    id: 'page:MultiDeepDive',
    type: 'page',
    keys: [
      '/review-performance/multi-deep-dive',
      'multi deep dive',
      'deep dive',
      'multi dimension',
      'bars',
      'cross dimensional',
      'drill down',
      'comparison',
      'multiple timeframes',
    ],
    version: '0.1-draft',
    title: 'Multi Deep Dive',
    summary:
      'Multi-dimensional bar drill — one metric across multiple dimension combinations and timeframes.',
    body: `Purpose: Multi Deep Dive is the power-user drill page. It presents one metric as bar charts across multiple dimension facets (e.g. channel × region × campaign) and timeframes simultaneously. It uses the model_tracking fact table.

Lead KPI: sales_roi__nsp__npv (same root as CP and Dynamics; Multi Deep Dive is structurally bar-oriented, not timeline-oriented).
Fact table: model_tracking.

Example questions that route HERE:
- "Show me Sales RoI by campaign across 4 different time periods."
- "Compare ROI across 3 regions and 2 channels."
- "Give me a bar breakdown of efficiency across all dimensions." (INFERRED)
- "I want to see the metric across every combination of filters."
- "Deep dive on ROI — break it down every way."

What does NOT belong here:
- Headline totals → Review KPIs.
- Spend overview → Marketing Invests.
- Sales decomposition stacks → Contribution.
- Temporal trend / driver attribution narrative → Dynamics.
- Campaign-level efficiency with driver bars → Campaign Performance.

Boundary ambiguity (Contribution vs MultiDeepDive — INFERRED): Contribution = stacked decomposition (what share goes to each driver); MultiDeepDive = bar comparisons across dimension combinations (how does the metric compare across X and Y). Use Contribution for "what drove sales?", MultiDeepDive for "how does it compare across dimensions?"`,
    seeAlso: ['kpi:sales_roi'],
  },
]
